/**
 * Photo Proxy Utility
 * 
 * Converts Google Places photo URLs to use the backend proxy.
 * This reduces API costs by caching photos on the backend (S3).
 * 
 * Handles three cases:
 * 1. Already proxied URLs (/api/photos/proxy) - returns as-is
 * 2. S3 URLs or other non-Google URLs - returns as-is
 * 3. Google URLs (places.googleapis.com, maps.googleapis.com) - converts to proxy
 */

/**
 * Convert a Google Places photo URL to use the proxy
 * 
 * @param photoUrl - The photo URL (can be Google URL, proxy URL, S3 URL, or null)
 * @returns The proxy URL if it's a Google URL, or the original URL if already proxied/S3/other
 */
export function convertToProxyUrl(photoUrl: string | null | undefined): string | null {
  if (!photoUrl) return null;
  
  // If it's already a proxy URL, return as-is
  // Proxy URLs look like: https://api.gridappsocial.com/api/photos/proxy?url=<encoded-google-url>
  if (photoUrl.includes('/api/photos/proxy')) {
    return photoUrl;
  }
  
  // If it's not a Google photo URL, return as-is
  // This includes S3 URLs, localhost URLs, and other image sources
  if (!photoUrl.includes('places.googleapis.com') && 
      !photoUrl.includes('maps.googleapis.com')) {
    return photoUrl;
  }
  
  // Get the API base URL based on environment
  const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
    ? process.env.REACT_APP_REMOTE_API_URL || 'https://api.gridappsocial.com'
    : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3000';
  
  // Encode the Google URL and return proxy URL
  // The proxy will redirect to S3, caching the photo for 30 days
  const encodedUrl = encodeURIComponent(photoUrl);
  return `${apiBaseUrl}/api/photos/proxy?url=${encodedUrl}`;
}

/**
 * Check if a URL is a Google Places photo URL
 */
export function isGooglePhotoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('places.googleapis.com') || url.includes('maps.googleapis.com');
}

/**
 * Check if a URL is already using the proxy
 */
export function isProxyUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('/api/photos/proxy');
}
